import React, { useState, useEffect } from 'react';
import { Modal, Form, Select, DatePicker, InputNumber, Button, Space, Tooltip, Row, Col, Divider, Avatar, Input, Alert } from 'antd';
import { EditOutlined, SaveOutlined, UserOutlined, UserAddOutlined, GlobalOutlined, CheckCircleOutlined, MailOutlined, PhoneOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import useCustomerStore from '../../../stores/customerStore';
import usePlatformStore from '../../../stores/platformStore';

const { RangePicker } = DatePicker;
const { TextArea } = Input;

/**
 * A reusable modal component for creating and editing bookings
 */
const BookingModal = ({ 
  isVisible, 
  onClose, 
  onSave, 
  initialValues = null, 
  title = "Booking", 
  submitting = false 
}) => {
  const [form] = Form.useForm();
  const [isNewCustomerModalVisible, setIsNewCustomerModalVisible] = useState(false);
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const [customerFormErrors, setCustomerFormErrors] = useState(null);
  const [newCustomerForm] = Form.useForm();

  // Get the required data from stores
  const { customers, fetchCustomers, createCustomer } = useCustomerStore();
  const { platforms, fetchPlatforms } = usePlatformStore();

  // Fetch customers and platforms when component mounts
  useEffect(() => {
    if (!customers.length) {
      fetchCustomers();
    }
    if (!platforms.length) {
      fetchPlatforms();
    }
  }, [customers.length, platforms.length, fetchCustomers, fetchPlatforms]);

  // Set form values when initial values change
  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue({
        ...initialValues,
        customer: initialValues.customer?.id || initialValues.customer || null,
        platform: initialValues.platform_details?.id || initialValues.platform,
        dates: [dayjs(initialValues.check_in), dayjs(initialValues.check_out)],
        total_price: initialValues.total_price ? parseFloat(initialValues.total_price) : null,
        net_price: initialValues.net_price ? parseFloat(initialValues.net_price) : null,
        status: initialValues.status
      });
    } else {
      form.resetFields();
    }
  }, [initialValues, form]);

  // Customer component for dropdown
  const CustomerOption = ({ customer }) => (
    <Space>
      <Avatar icon={<UserOutlined />} style={{ backgroundColor: '#1890ff' }} />
      <div>
        <div>{customer.name}</div>
        <div style={{ fontSize: '12px', color: '#999' }}>
          {customer.email} • {customer.phone}
        </div>
      </div>
    </Space>
  );

  // Customer select dropdown props
  const customerSelectProps = {
    placeholder: "Select a customer (optional)",
    options: customers.map(c => ({
      value: c.id,
      label: c.name, // Simple label for selected value
      data: c // Store full customer data for dropdown
    })),
    showSearch: true,
    allowClear: true,
    optionFilterProp: "children",
    filterOption: (input, option) => {
      const customer = customers.find(c => c.id === option.value);
      if (!customer) return false;
      return (
        customer.name.toLowerCase().includes(input.toLowerCase()) ||
        (customer.email && customer.email.toLowerCase().includes(input.toLowerCase())) ||
        (customer.phone && customer.phone.includes(input))
      );
    },
    suffixIcon: <UserOutlined />,
    optionLabelProp: "label", // Use simple label for selected value
    optionRender: (option) => { // Custom render for dropdown options
      const customer = customers.find(c => c.id === option.value);
      if (!customer) return null;
      return <CustomerOption customer={customer} />;
    },
    dropdownRender: menu => (
      <>
        {menu}
        <Divider style={{ margin: '8px 0' }} />
        <Button
          type="link"
          icon={<UserAddOutlined />}
          onClick={showNewCustomerModal}
          style={{ width: '100%', textAlign: 'left' }}
        >
          Create New Customer
        </Button>
      </>
    )
  };

  // Show customer creation modal
  const showNewCustomerModal = () => {
    setIsNewCustomerModalVisible(true);
    setCustomerFormErrors(null);
    newCustomerForm.resetFields();
  };

  // Handle customer creation
  const handleCreateCustomer = async () => {
    try {
      setCreatingCustomer(true);
      setCustomerFormErrors(null);
      const values = await newCustomerForm.validateFields();
      const result = await createCustomer(values);
      
      if (result.success) {
        await fetchCustomers();
        form.setFieldValue('customer', result.data.id);
        newCustomerForm.resetFields();
        setIsNewCustomerModalVisible(false);
      } else if (result.error) {
        // Set form errors from API response
        setCustomerFormErrors(result.error.response?.data || { general: 'An error occurred' });
      }
    } catch (error) {
      // Form validation error, no need to handle
    } finally {
      setCreatingCustomer(false);
    }
  };

  // Handle form submission
  const handleFormSubmit = async () => {
    try {
      const values = await form.validateFields();
      const [check_in, check_out] = values.dates;
      const bookingData = {
        ...values,
        check_in: check_in.format('YYYY-MM-DD'),
        check_out: check_out.format('YYYY-MM-DD'),
      };
      delete bookingData.dates;
      
      await onSave(bookingData);
    } catch (error) {
      console.error("Form validation failed:", error);
    }
  };

  return (
    <>
      <Modal
        title={title}
        open={isVisible}
        onCancel={onClose}
        onOk={handleFormSubmit}
        okText={submitting ? "Saving..." : "Save"}
        confirmLoading={submitting}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          requiredMark="optional"
        >
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="customer_name"
                label="Customer Name"
                style={{ width: '100%', marginBottom: 24 }}
              >
                <Input 
                  prefix={<UserOutlined />}
                  placeholder="Enter customer name"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="platform"
                label="Platform"
                rules={[{ required: true, message: 'Please select a platform!' }]}
              >
                <Select
                  placeholder="Select platform"
                  options={platforms.map(p => ({ value: p.id, label: p.name }))}
                  suffixIcon={<GlobalOutlined />}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="dates"
                label="Check-in/Check-out Dates"
                rules={[{ required: true, message: 'Please select the dates!' }]}
              >
                <RangePicker 
                  style={{ width: '100%' }} 
                  format="DD-MMM-YYYY"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="total_price"
                label="Total Price (Guest Pays)"
              >
                <InputNumber
                  prefix="$"
                  min={0}
                  precision={2}
                  style={{ width: '100%' }}
                  placeholder="Enter total price (optional)"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="net_price"
                label="Net Price (You Receive)"
              >
                <InputNumber
                  prefix="$"
                  min={0}
                  precision={2}
                  style={{ width: '100%' }}
                  placeholder="Enter net price (optional)"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="status"
                label="Status"
                rules={[{ required: true, message: 'Please select a status!' }]}
                initialValue="pending"
              >
                <Select
                  options={[
                    { value: 'pending', label: 'Pending' },
                    { value: 'confirmed', label: 'Confirmed' },
                    { value: 'Reserved', label: 'Reserved' },
                    { value: 'cancelled', label: 'Cancelled' },
                    { value: 'completed', label: 'Completed' },
                  ]}
                  suffixIcon={<CheckCircleOutlined />}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="notes"
            label="Notes"
          >
            <TextArea
              placeholder="Add notes about this booking"
              rows={3}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* New Customer Modal */}
      <Modal
        title="Create New Customer"
        open={isNewCustomerModalVisible}
        onOk={handleCreateCustomer}
        onCancel={() => {
          setIsNewCustomerModalVisible(false);
          setCustomerFormErrors(null);
        }}
        confirmLoading={creatingCustomer}
        okText={creatingCustomer ? "Saving..." : "Save"}
      >
        {customerFormErrors && (
          <Alert
            message="Error"
            description={
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                {Object.entries(customerFormErrors).map(([field, errors]) => (
                  <li key={field}>
                    <strong>{field}:</strong> {Array.isArray(errors) ? errors.join(', ') : errors}
                  </li>
                ))}
              </ul>
            }
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            closable
            onClose={() => setCustomerFormErrors(null)}
          />
        )}
        
        <Form
          form={newCustomerForm}
          layout="vertical"
        >
          <Form.Item
            name="name"
            label="Full Name"
            rules={[{ required: true, message: 'Please input the customer name!' }]}
          >
            <Input 
              prefix={<UserOutlined />}
              placeholder="Enter customer's full name"
              autoFocus
            />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email Address"
            rules={[
              { type: 'email', message: 'Please enter a valid email!' }
            ]}
            extra="We'll use this email for booking confirmations"
          >
            <Input
              prefix={<MailOutlined />}
              placeholder="customer@example.com"
            />
          </Form.Item>

          <Form.Item
            name="phone"
            label="Phone Number"
            extra="Include country code for international numbers"
          >
            <Input
              prefix={<PhoneOutlined />}
              placeholder="+1 234 567 890"
            />
          </Form.Item>

          <Form.Item
            name="notes"
            label="Additional Notes"
          >
            <TextArea
              placeholder="Any special requirements or notes about the customer"
              rows={3}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default BookingModal;